import type { Subscription } from '@adonisjs/transmit-client'
import type { SocketChannelEvents, SocketEventDataMap, SocketMessage, UseChannelChannels, UseChannelToBaseType, UseChannelToConcreteChannel } from '@thecompaniesapi/shared/client'

export function useSockets() {
  const nuxtApp = useNuxtApp()

  const socket = nuxtApp?.$socket

  return socket
}

// Hook to subscribe to channel events
export function useEvent<
  T extends UseChannelChannels,
  E extends SocketChannelEvents[UseChannelToBaseType<T>] = SocketChannelEvents[UseChannelToBaseType<T>],
>(
  channel: ComputedRef<T | undefined> | MaybeRef<T | undefined>,
  targetEvent: E | E[],
  callback: (message: SocketEventDataMap[E], event: E) => Promise<void> | void,
  options?: {
    debounce?: number
  },
) {
  const sockets = useSockets()
  const cancelOnMessage = shallowRef<(() => void) | undefined>()
  const subscription = shallowRef<Subscription | undefined>()

  targetEvent = Array.isArray(targetEvent) ? targetEvent : [targetEvent]

  const register = async (channel: T) => {
    if (!sockets.transmit.value) {
      return
    }

    if (channel === 'global') {
      subscription.value = sockets.globalSubscription.value
    }
    else if (channel === 'team') {
      subscription.value = sockets.teamSubscription.value
    }
    else if (channel === 'user') {
      subscription.value = sockets.userSubscription.value
    }
    else if (channel === 'visitor') {
      subscription.value = sockets.visitorSubscription.value
    }
    else if (channel?.startsWith?.('lists/')) {
      subscription.value = sockets.listsSubscriptions[channel]

      if (!subscription.value) {
        subscription.value = sockets.transmit.value.subscription(channel)

        sockets.listsSubscriptions[channel] = subscription.value

        await subscription.value.create()
      }
    }

    if (!subscription.value) {
      return
    }

    // Debounce the callback if requested
    if (options?.debounce) {
      callback = useDebounceFn(callback, options.debounce)
    }

    cancelOnMessage.value = subscription.value.onMessage(async (message: string) => {
      sockets.lastEvent.value = Date.now()

      let messagePayload: SocketMessage<UseChannelToConcreteChannel<T>> | undefined

      try {
        messagePayload = JSON.parse(message)
      }
      catch {}

      try {
        // Call the callback if it exists and the message payload is valid
        // As the data comes from the socket here, we have to cast it with `as`
        if (callback && messagePayload && targetEvent.includes(messagePayload.event as E)) {
          await callback(
            messagePayload.data as SocketEventDataMap[E],
            messagePayload.event as E,
          )
        }
      }
      catch {
        console.warn('Could not handle server event', { targetEvent })
      }
    })
  }

  const unregister = async (channel: string) => {
    if (cancelOnMessage.value) {
      cancelOnMessage.value()
    }

    // Delete local list subscription
    if (channel?.startsWith?.('lists/') && subscription.value) {
      try {
        await subscription.value.delete()

        delete sockets.listsSubscriptions[channel]
      }
      catch {}
    }
  }

  onUnmounted(async () => {
    const currentChannel = unref(channel)

    if (currentChannel) {
      await unregister(currentChannel)
    }
  })

  onMounted(async () => {
    const currentChannel = unref(channel)

    if (currentChannel) {
      await register(currentChannel)
    }
  })

  watch(
    () => unref(channel),
    async (newChannel, oldChannel) => {
      if (oldChannel) {
        await unregister(oldChannel)
      }

      if (newChannel) {
        await register(newChannel)
      }
    },
  )
}
